import { ProductOptionModel, SelectionImageModel } from "@tSurya/Store/Data/ConfiguratorModels";

/** Removes the SFCC '~~oId' uniqueness suffix (e.g. 'XXX~~10' -> 'XXX'). */
function stripUniqueSuffix(valueId: string): string {
    if (!valueId) {
        return valueId;
    }
    const i = valueId.indexOf("~~");
    return i >= 0 ? valueId.substring(0, i) : valueId;
}

/**
 * Resolves the best-matching selection-driven image for the current selections. Mirrors the
 * backend SelectionImageResolver: an image's variation attribute-id is ignored and its value is
 * a value id. Uses a best/partial strategy (most matching values wins); images with no matching
 * value are excluded. All images tied for the best score and specificity are returned in sort order.
 */
export function resolveSelectionImages(
    selections: Record<string, string> | null | undefined,
    _options: unknown,
    selectionImages: SelectionImageModel[] | null | undefined,
): SelectionImageModel[] {
    if (!selections || !selectionImages || selectionImages.length === 0) {
        return [];
    }

    const selectedValues = new Set(
        Object.values(selections).map(valueId => stripUniqueSuffix(valueId).toLowerCase()),
    );

    if (selectedValues.size === 0) {
        return [];
    }

    const candidates: Array<{ image: SelectionImageModel; score: number; pairs: number }> = [];

    for (const image of selectionImages) {
        const pairs = image.selections ? Object.entries(image.selections) : [];
        if (pairs.length === 0) {
            continue;
        }

        let score = 0;
        for (const [, value] of pairs) {
            if (selectedValues.has(stripUniqueSuffix(value).toLowerCase())) {
                score++;
            }
        }

        if (score === 0) {
            continue;
        }

        candidates.push({ image, score, pairs: pairs.length });
    }

    candidates.sort(
        (left, right) =>
            right.score - left.score || right.pairs - left.pairs || left.image.sortOrder - right.image.sortOrder,
    );

    const best = candidates[0];
    if (!best) {
        return [];
    }

    return candidates
        .filter(candidate => candidate.score === best.score && candidate.pairs === best.pairs)
        .map(candidate => candidate.image);
}

/** Returns the first best-match image for consumers that require a single preview. */
export function resolveSelectionImage(
    selections: Record<string, string> | null | undefined,
    options: ProductOptionModel[] | null | undefined,
    selectionImages: SelectionImageModel[] | null | undefined,
): SelectionImageModel | null {
    return resolveSelectionImages(selections, options, selectionImages)[0] || null;
}
